// Categorical color assignment for an arbitrary, dashboard-defined series list.
// A BI renderer colors series it cannot know at compile time, so the mechanism
// is the SLOT, not the entity: 8 dataviz-validated categorical CSS custom
// properties (tokens.css --rc-chart-1..8), assigned to series keys in DECLARED
// order.
import { useMemo } from "react";

/** The 8 fixed-order categorical chart slots (tokens.css --rc-chart-1..8).
 *  Order is the CVD-safety mechanism (dataviz skill, color-formula.md check 1)
 *  — it is a validated SEQUENCE, never re-sorted or hashed at runtime. */
export const CHART_CATEGORICAL_SLOTS = 8 as const;

/** The CSS var for categorical slot `index` (0-based), or the shared neutral
 *  fold-in slot once every validated slot is spent. Per the dataviz skill: a
 *  9th+ series is NEVER a generated/cycled hue — it folds to "Other".
 *
 *  Callers of an ALL-PAIRS chart form (scatter, bubble, heatmap-by-category,
 *  donut, small multiples — forms where any two marks can sit side by side)
 *  MUST cap their series list to 3 before calling this or assignCategoricalColors:
 *  the palette only clears all-pairs CVD separation on its first 3 slots (see
 *  the comment on --rc-chart-1..8 in tokens.css). Only ADJACENT forms (line,
 *  bar, stacked area, legend) may use the full 8. */
export function categoricalSlotVar(index: number): string {
  return index < CHART_CATEGORICAL_SLOTS ? `var(--rc-chart-${index + 1})` : "var(--rc-chart-other)";
}

/** Assigns each series key a slot IN THE ORDER GIVEN. `seriesKeys` must be the
 *  chart's DECLARED series order (from the BI chart spec's field/series list),
 *  never a runtime-derived order (sorted by value, or first-seen-in-data) —
 *  that would reshuffle colors as data changes, exactly what "never cycle"
 *  forbids. Two different charts assign independently; there is no cross-chart
 *  color-identity guarantee (a renderer that wants the SAME series to always
 *  wear the same color across charts must pass the same declared order to
 *  every chart that shows it). */
export function assignCategoricalColors(seriesKeys: readonly string[]): Map<string, string> {
  const map = new Map<string, string>();
  seriesKeys.forEach((key, i) => map.set(key, categoricalSlotVar(i)));
  return map;
}

/** Stable per-mount color map. Memoized on the series key list's membership
 *  AND order (a joined string, not array identity), so it never reshuffles on
 *  a data refresh that keeps the same series. */
export function useCategoricalColors(seriesKeys: readonly string[]): Map<string, string> {
  const cacheKey = seriesKeys.join("\u0000");
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the joined string on purpose, not array identity
  return useMemo(() => assignCategoricalColors(seriesKeys), [cacheKey]);
}

// SEQUENTIAL (ordinal) series
//
// Everything above answers "WHICH series is this?" and is capped at 8 because a
// reader cannot hold a 9th categorical answer. An ORDINAL series set asks a
// different question - "which is EARLIER / SMALLER?" - and the answer is a
// POSITION, not an identity. Twenty-one date cohorts drawn in 21 unrelated hues
// are less readable than 21 steps of one ramp, and folding them to "top 7 +
// Other" throws away the very thing the chart is about. So the categorical cap
// does not apply here, and neither does the categorical palette.
//
// WHAT THIS IS NOT: a way around the 8-slot rule. A caller reaches for this only
// when the series have a genuine total order the reader will read off the ramp;
// `pivot.ts` (lib/pivot.ts, isOrdinalSeriesSet) restricts that to CALENDAR DATES
// on purpose. A numeric id column sorts fine and is still categorical - colored
// as a ramp it would imply an adjacency between id 4 and id 5 that does not
// exist.

/** One channel of the ramp, interpolated BY THE BROWSER between the theme's two
 *  endpoint custom properties. Held as unitless components (tokens.css
 *  --rc-chart-seq-*) rather than as two colors precisely so this can happen in
 *  CSS: the ramp stays re-pointable per theme, so a tier that changes
 *  --rc-color-surface re-points six numbers and every step moves with it,
 *  exactly as the 8 categorical slots already do. */
function rampChannel(from: string, to: string, t: number): string {
  return `calc(var(${from}) + (var(${to}) - var(${from})) * ${t})`;
}

/** The color for ordinal position `index` of `count`, as a CSS `hsl()` whose
 *  channels are computed from the theme's ramp endpoints. A single-item set sits
 *  at the ramp's START (t=0) rather than dividing by zero.
 *
 *  Linear in HSL, not in a perceptual space, because it MATCHES A PUBLISHED
 *  CHART: the "Mira vs Job Post GOA" artifact interpolates its cohort curves
 *  this way and the BI replica has to draw the same picture. oklab would step
 *  more evenly and would visibly disagree at the light end, so it is the wrong
 *  choice HERE specifically - not a general recommendation. The artifact rounds
 *  each channel to an integer and this does not; that is under 1% per channel
 *  and not perceivable. */
export function sequentialRampVar(index: number, count: number): string {
  const t = count <= 1 ? 0 : Math.min(Math.max(index, 0), count - 1) / (count - 1);
  const h = rampChannel("--rc-chart-seq-h-from", "--rc-chart-seq-h-to", t);
  // `calc(<number> * 1%)` is how a unitless custom property becomes the
  // percentage hsl() requires for S and L; hsl() takes a plain number for H.
  const s = `calc((${rampChannel("--rc-chart-seq-s-from", "--rc-chart-seq-s-to", t)}) * 1%)`;
  const l = `calc((${rampChannel("--rc-chart-seq-l-from", "--rc-chart-seq-l-to", t)}) * 1%)`;
  return `hsl(${h} ${s} ${l})`;
}

/** Assigns each series key a ramp position IN THE ORDER GIVEN, which for an
 *  ordinal set MUST be the ordinal order (earliest cohort first). Unlike the
 *  categorical case, order here is not merely a stable-color convention - it IS
 *  the encoding. There is no "Other" slot: every series gets a real position,
 *  because a ramp has as many steps as it needs. */
export function assignSequentialColors(seriesKeys: readonly string[]): Map<string, string> {
  const map = new Map<string, string>();
  seriesKeys.forEach((key, i) => map.set(key, sequentialRampVar(i, seriesKeys.length)));
  return map;
}

/** Stable per-mount ramp map. Memoized on membership AND order like
 *  `useCategoricalColors`, so a refresh returning the same cohorts redraws them
 *  identically. A NEW cohort arriving legitimately shifts every step, since the
 *  ramp renormalizes over the new count - correct for a ramp, and exactly what
 *  would be wrong for identity colors. */
export function useSequentialColors(seriesKeys: readonly string[]): Map<string, string> {
  const cacheKey = seriesKeys.join("\u0000");
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the joined string on purpose, not array identity
  return useMemo(() => assignSequentialColors(seriesKeys), [cacheKey]);
}
